import type { RecoveryPoint } from "./types";
// API 客户端：fetch + Bearer token（localStorage csp.token），401 自动回登录页
import type {
  TaskFileDTO,
  FieldChatMessage,
  BrowserSlotDTO,
  Priority,
  ProposalDTO,
  ReviewDTO,
  SkillVersionDTO,
  SopItemDTO,
  SponsorMailDTO,
  TaskDTO,
  TaskTypeDTO,
  UserDTO,
} from "@csp/shared";
import { apiUrl } from "./net";
import type {
  AdminSettings,
  AnalysisItem,
  CompareColumn,
  HealthItem,
  LoginResult,
  ProposalDetail,
  RunDetail,
  SystemInfo,
  TaskSearchResult,
  TaskStats,
  TypeDetail,
  TypeTimeline,
  UploadTicket,
} from "./types";

const TOKEN_KEY = "csp.token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(t: string | null): void {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* 隐私模式下忽略 */
  }
}

/** 后端错误码 → 文员看得懂的话（后端已给 message 时优先用 message） */
const CODE_TEXT: Record<string, string> = {
  unauthorized: "登录已过期，请重新登录",
  forbidden: "没有权限做这个操作",
  urgent_requires_boss: "只有老板或管理员可以设为「紧急」",
  not_reviewer: "这个检查点不归你审核",
  takeover_clerk_only: "只有文员可以手动接管",
  clerk_only: "只有文员可以处理卡住的任务",
  admin_only: "这一项需要管理员处理",
  l3_admin_only: "成熟类型（L3）的改动只能由管理员采纳",
  clerk_adopt_locked: "该类型近期改动较多或通过率在下降，暂时只能由管理员采纳",
  major_change_admin_only: "改动较大，需要管理员审批",
  review_closed: "这个检查点已经处理过了",
  task_not_in_review: "任务已不在审核状态",
  not_blocked: "任务已不在卡住状态",
  not_waiting_scan: "任务已不在等扫码状态",
  materials_missing: "材料还不齐",
  not_ready: "任务还不能开始",
  type_disabled: "该任务类型尚未启用",
  task_closed: "任务已结束，不能再上传材料",
  bad_transition: "当前状态下不能这样操作",
  stale_revision: "数据已更新，请刷新后重试",
  not_editable: "只有草稿 / 就绪 / 卡住的任务可以修改",
  invalid_input: "填写内容有误",
  internal_error: "服务器出错了，请稍后再试",
  network_error: "网络连接失败，请检查网络",
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

/** 版本冲突（规范 §3.3）的统一提示语 */
export const CONFLICT_TEXT = "数据已更新，请刷新后重试";

/** 是否「别人先改过了」的 409：带 expectedUpdatedAt 的写操作被拒。其它 409（如状态不允许）不算 */
export function isStaleConflict(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && (e.code === "stale_revision" || e.message.includes("刷新"));
}

export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res: Response;
  try {
    res = await fetch(apiUrl(path), { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "network_error", CODE_TEXT.network_error as string);
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!res.ok) {
    const b = (data ?? {}) as { error?: string; message?: string };
    const code = b.error ?? `http_${res.status}`;
    if (res.status === 401 && !path.startsWith("/api/auth/login")) {
      setToken(null);
      onUnauthorized?.();
    }
    const msg = b.message && b.message !== code ? b.message : (CODE_TEXT[code] ?? `请求失败（${res.status} ${code}）`);
    throw new ApiError(res.status, code, msg);
  }
  return data as T;
}

const get = <T>(p: string) => request<T>("GET", p);
const post = <T>(p: string, b: unknown = {}) => request<T>("POST", p, b);
const put = <T>(p: string, b: unknown) => request<T>("PUT", p, b);
const patch = <T>(p: string, b: unknown) => request<T>("PATCH", p, b);
const enc = encodeURIComponent;

export interface CreateTaskBody {
  typeId: string;
  clientEntity: { name: string; uen: string };
  title?: string;
  priority: Priority;
  manifest: Record<string, unknown>;
  materialsNote: string;
  dryRun?: boolean;
  start?: boolean;
  /** Corppass 任务分配给谁（Chrome id） */
  assignee?: string | null;
}

export type StuckAction = "takeover" | "handled" | "data" | "process" | "unknown" | "give_up" | "retry";
export type ReviewAction = "approve" | "revise" | "takeover" | "takeover_done" | "cancel" | "end";

export const api = {
  // 账号
  login: (username: string, password: string) => post<LoginResult>("/api/auth/login", { username, password, client: "web" }),
  logout: () => post<{ ok: boolean }>("/api/auth/logout"),
  me: () => get<UserDTO>("/api/auth/me"),
  changePassword: (oldPassword: string, newPassword: string) => post("/api/auth/password", { oldPassword, newPassword }),
  users: () => get<UserDTO[]>("/api/users"),
  createUser: (b: { username: string; displayName: string; role: string; password: string; whatsapp?: string | null }) =>
    post<UserDTO>("/api/users", b),
  updateUser: (id: string, b: Partial<{ displayName: string; role: string; whatsapp: string | null; active: boolean; password: string }>) =>
    patch<UserDTO>(`/api/users/${enc(id)}`, b),

  // 任务类型
  types: (all = false) => get<TaskTypeDTO[]>(`/api/types${all ? "?all=1" : ""}`),
  type: (id: string) => get<TypeDetail>(`/api/types/${enc(id)}`),
  firstRun: (id: string, formal = false, title = "") => post<{ taskId: string }>(`/api/types/${id}/first-run`, { start: false, formal, title }),
  typeMemory: (id: string) => get<{ content: string }>(`/api/types/${enc(id)}/memory`),
  saveTypeMemory: (id: string, content: string, why: string) => put<{ content: string; commit: string }>(`/api/types/${enc(id)}/memory`, { content, why }),
  /** 担保人邮箱转发状态；refresh = 先回扫一次 ss@kaider.sg */
  sponsorMails: (refresh = false) => get<SponsorMailDTO[]>(`/api/sponsor-mails${refresh ? "?refresh=1" : ""}`),
  /** 一次性验证码：后端直接填进页面（秒级）；resend = 点重新发送 */
  otp: (id: string, body: { code?: string; resend?: boolean }) => post<{ state: "verified" | "unknown" | "expired" | "invalid" | "no_input" | "resent" | "resend_ignored" | "handed_over"; leftSec?: number | null }>(`/api/tasks/${enc(id)}/otp`, body),
  learningAnswer: (id: string, answer: string, fileIds: string[] = [], readFileIds: string[] = []) => post<{ ok: boolean }>(`/api/tasks/${id}/learning-answer`, { answer, fileIds, readFileIds }),
  /** 运行中问到的缺字段：字段表里没有就登记，返回字段 key */
  learningField: (id: string) => post<{ key: string; added: boolean; /** 下拉 / 单选字段的页面选项（直接点选） */ options?: string[] }>(`/api/tasks/${enc(id)}/learning-field`),
  assignFile: (fileId: string, key: string) => post<TaskFileDTO>(`/api/files/${enc(fileId)}/assign`, { key }),
  /** 压缩后的上传副本看过、看得清 */
  confirmUploadCopy: (fileId: string) => post<{ ok: boolean }>(`/api/files/${enc(fileId)}/upload-copy/confirm`),
  /** 文件压缩后看不清 / 压不下来：叫管理员 */
  fileHelp: (fileId: string, note = "") => post<{ ok: boolean }>(`/api/files/${enc(fileId)}/help`, { note }),
  addFileCategory: (id: string, label: string) => post<{ key: string; created: boolean }>(`/api/tasks/${id}/file-category`, { label }),
  stepFeedback: (id: string, runId: string, step: number, note: string) => post<{ ok: boolean }>(`/api/tasks/${id}/step-feedback`, { runId, step, note }),
  feedbackSop: (id: string, runId: string, apply = false, expectedCommit?: string) => post<{ content: string; baseCommit: string; version?: string }>(`/api/tasks/${id}/feedback-sop`, { runId, apply, expectedCommit }),
  createType: (b: unknown) => post<TaskTypeDTO>("/api/types", b),
  updateType: (id: string, b: unknown) => patch<TaskTypeDTO>(`/api/types/${enc(id)}`, b),
  sampleFromFile: (typeId: string, key: string, fileId: string) => post<TaskTypeDTO>(`/api/types/${enc(typeId)}/files/${enc(key)}/samples/from-file`, { fileId }),
  removeSample: (typeId: string, key: string, sampleId: string) => request<TaskTypeDTO>("DELETE", `/api/types/${enc(typeId)}/files/${enc(key)}/samples/${enc(sampleId)}`),
  /** 上传样本：原始字节直传后端（样本小，不走 R2 直传） */
  addSample: async (typeId: string, key: string, file: File): Promise<TaskTypeDTO> => {
    const token = getToken();
    const res = await fetch(apiUrl(`/api/types/${enc(typeId)}/files/${enc(key)}/samples?name=${enc(file.name)}`), {
      method: "POST",
      headers: { "Content-Type": file.type || "application/octet-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: file,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, data?.error ?? `http_${res.status}`, data?.message ?? `上传失败（${res.status}）`);
    return data as TaskTypeDTO;
  },
  skill: (id: string, commit?: string) =>
    get<{ content: string | null; commit?: string | null; version?: string }>(`/api/types/${enc(id)}/skill${commit ? `?commit=${enc(commit)}` : ""}`),
  editSkill: (id: string, content: string, why: string, bump: "patch" | "minor") =>
    put<SkillVersionDTO>(`/api/types/${enc(id)}/skill`, { content, why, bump }),
  versions: (id: string) => get<SkillVersionDTO[]>(`/api/types/${enc(id)}/versions`),
  sopItems: (id: string) => get<SopItemDTO[]>(`/api/types/${enc(id)}/sop-items`),
  decideSopItem: (itemId: string, b: { status: "pending" | "accepted" | "rejected"; detail?: string }) => post<SopItemDTO>(`/api/sop-items/${enc(itemId)}/decide`, b),
  addSopItem: (id: string, b: { kind: "step" | "rule"; detail: string }) => post<SopItemDTO>(`/api/types/${enc(id)}/sop-items`, b),
  applySopItems: (id: string) => post<{ version: string; count: number }>(`/api/types/${enc(id)}/sop-items/apply`, {}),
  rollback: (id: string, b: { versionId?: string; golden?: boolean; why: string; dryRun: boolean }) =>
    post<{ version: SkillVersionDTO; dryRunTaskId: string | null }>(`/api/types/${enc(id)}/rollback`, b),
  dryRun: (id: string) => post<{ taskId: string }>(`/api/types/${enc(id)}/dry-run`),
  compare: (id: string, versionId: string) => post<{ group: string; taskIds: string[] }>(`/api/types/${enc(id)}/compare`, { versionId }),
  compareResult: (group: string) => get<CompareColumn[]>(`/api/compare/${enc(group)}`),
  typeTimeline: (id: string, days = 90) => get<TypeTimeline>(`/api/types/${enc(id)}/timeline?days=${days}`),
  analyze: (id: string, runId?: string | null) => post<{ id: string; text: string; createdAt: string }>(`/api/types/${enc(id)}/analyze`, runId ? { runId } : {}),

  // 任务
  tasks: (q: { status?: string; q?: string; mine?: boolean; test?: "only" | "all" } = {}) => {
    const p = new URLSearchParams();
    if (q.test) p.set("test", q.test);
    if (q.status) p.set("status", q.status);
    if (q.q) p.set("q", q.q);
    if (q.mine) p.set("mine", "1");
    const s = p.toString();
    return get<TaskDTO[]>(`/api/tasks${s ? `?${s}` : ""}`);
  },
  /** 全部任务：多条件筛选 + 分页（查询串由 lib/taskQuery 的 filtersToParams 生成） */
  searchTasks: (params: URLSearchParams) => get<TaskSearchResult>(`/api/tasks/search?${params.toString()}`),
  /** 任务统计：granularity / from / to / typeId / q */
  taskStats: (params: URLSearchParams) => get<TaskStats>(`/api/tasks/stats?${params.toString()}`),
  /** 创建人筛选选项 */
  taskCreators: () => get<{ id: string; displayName: string }[]>("/api/tasks/creators"),
  task: (id: string) => get<TaskDTO>(`/api/tasks/${enc(id)}`),
  createTask: (b: CreateTaskBody) => post<TaskDTO>("/api/tasks", b),
  /** expectedUpdatedAt = 读到的 updatedAt；期间被别人改过后端返回 409（见 isStaleConflict） */
  updateTask: (
    id: string,
    b: Partial<Pick<CreateTaskBody, "clientEntity" | "title" | "priority" | "manifest" | "materialsNote" | "assignee">> & { expectedUpdatedAt?: string },
  ) =>
    patch<TaskDTO>(`/api/tasks/${enc(id)}`, b),
  startTask: (id: string) => post<TaskDTO>(`/api/tasks/${enc(id)}/start`),
  /** 拉起待命浏览器；force = 重新拉起 */
  standby: (id: string, force = false) => post<TaskDTO>(`/api/tasks/${enc(id)}/standby`, { force }),
  /** 执行端的 Chrome 及占用情况 */
  browsers: () => get<BrowserSlotDTO[]>("/api/browsers"),
  cancelTask: (id: string, note?: string) => post<TaskDTO>(`/api/tasks/${enc(id)}/cancel`, note ? { note } : {}),
  setPriority: (id: string, priority: Priority, expectedUpdatedAt?: string) => post<TaskDTO>(`/api/tasks/${enc(id)}/priority`, { priority, expectedUpdatedAt }),
  archiveTask: (id: string) => post<{ ok: boolean }>(`/api/tasks/${enc(id)}/archive`),
  removeTask: (id: string) => post<{ ok: boolean }>(`/api/tasks/${enc(id)}/remove`),
  requestUpload: (id: string, b: { key: string; name: string; contentType: string; size: number }) =>
    post<UploadTicket>(`/api/tasks/${enc(id)}/files`, b),
  completeUpload: (fileId: string) => post<{ id: string; state: string; sha256: string | null }>(`/api/files/${enc(fileId)}/complete`),
  removeFile: (fileId: string) => request<{ ok: boolean }>("DELETE", `/api/files/${enc(fileId)}`),
  restartTask: (id: string) => post(`/api/tasks/${enc(id)}/restart`),
  /** 重新提取材料信息 */
  extractTask: (id: string, quick = false) => post<TaskDTO>(`/api/tasks/${enc(id)}/extract${quick ? "?quick=1" : ""}`),
  discoverTask: (id: string) => post<TaskDTO>(`/api/tasks/${enc(id)}/discover`),
  /** 人工确定字段值；value=null 撤销人工值 */
  teachField: (id: string, key: string, message: string, fileIds: string[] = []) => post<{ chat: FieldChatMessage[] }>(`/api/tasks/${enc(id)}/fields/${enc(key)}/teach`, { message, fileIds }),
  confirmTeach: (id: string, key: string) => post<TaskDTO>(`/api/tasks/${enc(id)}/fields/${enc(key)}/teach/confirm`),
  setExtractedField: (id: string, key: string, value: string | null) => post<TaskDTO>(`/api/tasks/${enc(id)}/extraction/field`, { key, value }),
  resumeTask: (id: string, instruction = "", pointId?: string) => post(`/api/tasks/${enc(id)}/resume`, { instruction, pointId }),
  recoveryPoints: (id: string) => get<RecoveryPoint[]>(`/api/tasks/${enc(id)}/recovery-points`),
  saveRecoveryPoint: (id: string, input: { runId: string; source: "audit" | "timeline"; index: number; name: string; instruction: string }) => post(`/api/tasks/${enc(id)}/recovery-points`, input),
  scanRefresh: (id: string) => post(`/api/tasks/${enc(id)}/scan/refresh`),
  scanRestart: (id: string, auto = false) => post(`/api/tasks/${enc(id)}/scan/restart`, auto ? { auto } : {}),
  scanConfirm: (id: string) => post(`/api/tasks/${enc(id)}/scan/confirm`),
  scanFrame: (id: string) => post(`/api/tasks/${enc(id)}/scan/frame`),
  stuck: (id: string, action: StuckAction, note?: string) => post<TaskDTO>(`/api/tasks/${enc(id)}/stuck`, note ? { action, note } : { action }),
  taskRuns: (id: string) => get<RunDetail[]>(`/api/tasks/${enc(id)}/runs`),
  run: (id: string) => get<RunDetail>(`/api/runs/${enc(id)}`),
  pauseTask: (id:string)=>post<{ok:true}>(`/api/tasks/${enc(id)}/pause`),
  runHistory: (id: string) => get<{ role: string; text: string; at: string | null; model?: string }[]>(`/api/runs/${enc(id)}/history`),

  // 审核
  decide: (reviewId: string, action: ReviewAction, note?: string) =>
    post<ReviewDTO>(`/api/reviews/${enc(reviewId)}/decide`, note ? { action, note } : { action }),

  // SOP 提议
  proposal: (id: string) => get<ProposalDetail>(`/api/proposals/${enc(id)}`),
  adopt: (id: string, b: { content?: string; note?: string } = {}) => post<SkillVersionDTO>(`/api/proposals/${enc(id)}/adopt`, b),
  reject: (id: string, note?: string) => post<{ ok: boolean }>(`/api/proposals/${enc(id)}/reject`, note ? { note } : {}),
  escalate: (id: string) => post<{ ok: boolean }>(`/api/proposals/${enc(id)}/escalate`),

  // 管理员
  health: () => get<HealthItem[]>("/api/admin/health"),
  analyses: (typeId?: string) => get<AnalysisItem[]>(`/api/admin/analyses${typeId ? `?typeId=${enc(typeId)}` : ""}`),
  latestRun: (typeId: string) => get<{ runId: string; taskId: string } | null>(`/api/admin/latest-run/${enc(typeId)}`),
  settings: () => get<AdminSettings>("/api/admin/settings"),
  updateSettings: (b: Partial<Pick<AdminSettings, "whatsappClerks" | "whatsappBoss" | "paused">>) => put<{ ok: boolean }>("/api/admin/settings", b),
  agentsMd: () => get<{ content: string | null; sha: string | null }>("/api/admin/agents-md"),
  updateAgentsMd: (content: string, why: string) => put<{ commit: string }>("/api/admin/agents-md", { content, why }),
  system: () => get<SystemInfo>("/api/admin/system"),
  waLoginStart: () => post<{ qrDataUrl: string | null; sessionKey: string | null; message: string }>("/api/admin/whatsapp/login-start"),
  waLoginWait: (sessionKey: string | null) => post<{ connected: boolean; message: string }>("/api/admin/whatsapp/login-wait", { sessionKey }),
  waTest: (to: string) => post<{ ok: boolean }>("/api/admin/whatsapp/test", { to }),
};

export type { ProposalDTO };
